import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUserContext } from '../context/UserContext';
import { useRolesContext } from '../context/RolesContext';
import { useStatusContext } from '../context/StatusContext';
import { useContractorsContext } from '../context/ContractorsContext';
import {
  faHouse,
  faTasks,
  faUsers,
  faArchive,
  faBell,
  faComments,
  faGear,
  faUserShield,
  faChevronLeft,
  faChevronRight,
  faPlus,
  faRightFromBracket,
  faHouseChimney,
  faChartLine,
  faTag,
  faCartShopping,
  faFolderOpen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const SidebarSkeleton = () => (
  <div className="sidebar__nav">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="sidebar__skeleton-item">
        <div className="skeleton skeleton--icon" />
        <div className="skeleton skeleton--text" />
      </div>
    ))}
  </div>
);

const Sidebar = ({ onCreateTask }) => {
  const { user, isAuthenticated, role, roleId, logout, getUsers } = useUserContext();
  const { roles, getRoles } = useRolesContext();
  const { getStatus } = useStatusContext();
  const { getContractors } = useContractorsContext();
  const [collapsed, setCollapsed] = useState(false);
  const [roleType, setRoleType] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const location = useLocation();
  const winner = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      setDataLoading(true);
      Promise.all([getRoles(), getUsers(), getStatus(), getContractors()])
        .finally(() => setDataLoading(false));
      winner.current = window.innerWidth;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (roles?.length) {
      const idx = roles.findIndex((r) => r._id === roleId);
      if (idx !== -1) {
        setRoleType(roles[idx].type);
      }
    }
  }, [roles, roleId]);

  const FILE_URL = 'https://file.xn--b1afk7ai.xn--p1ai/';
  const openFiles = () => {
    window.open(FILE_URL, '_blank', 'noopener,noreferrer');
  };

  useEffect(()=>{
    if (winner.current<721)
    setCollapsed(true)
    // console.log(winner)
  }, [winner])

  const isAdmin = roleType === 'firstAdmin' || roleType === 'admin';

  const navItems = [
    { to: '/',       icon: faHouseChimney, label: 'Домашняя' },  // стартовая
    { to: '/board',  icon: faTasks,        label: 'Задачи' },    // канбан
    { to: '/contractors', icon: faUsers, label: 'Контрагенты' },
    { to: '/price', icon: faTag, label: 'Прайс' },
    { to: '/orders', icon: faCartShopping, label: 'Заказы' },
    { to: '/flud', icon: faComments, label: 'Флудилка' },
    ...(isAuthenticated && isAdmin
      ? [{ to: '/archive', icon: faArchive, label: 'Архив' }]
      : []),
    ...(isAuthenticated && isAdmin
      ? [{ to: '/reports', icon: faChartLine, label: 'Отчёты' }]
      : []),
    ...(isAuthenticated && isAdmin
      ? [{ to: '/notifications', icon: faBell, label: 'test' }]
      : []),
    ...(isAuthenticated && isAdmin
      ? [{ to: '/adminpanel', icon: faUserShield, label: 'Администрирование' }]
      : []),
  ];

  return (
    <aside
      className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''}`}
    >
      {/* Логотип */}
      <div className="sidebar__logo">
        {!collapsed && (
          <div className="sidebar__logo-text">
            <span className="sidebar__logo-title">TASKBOARD</span>
            <span className="sidebar__logo-sub">Внутренний портал</span>
          </div>
        )}
        <div className="sidebar__logo-icon">
          <FontAwesomeIcon icon={faHouse} />
        </div>
      </div>

      {/* Кнопка Свернуть */}
      <button
        className="sidebar__collapse-btn"
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Развернуть' : 'Свернуть'}
      >
        <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} />
        {!collapsed && <span>Свернуть</span>}
      </button>

      {/* Кнопка добавить */}
      {isAuthenticated && (
        <button
          className="sidebar__add-btn"
          onClick={onCreateTask}
          title="Создать новую задачу"
        >
          <FontAwesomeIcon icon={faPlus} />
          {!collapsed && <span>Добавить запись</span>}
        </button>
      )}
      

      {/* Навигация */}
      {dataLoading ? (
        <SidebarSkeleton />
      ) : (
        <nav className="sidebar__nav">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`sidebar__nav-item ${location.pathname === item.to ? 'sidebar__nav-item--active' : ''}`}
              title={collapsed ? item.label : ''}
            >
              <FontAwesomeIcon icon={item.icon} className="sidebar__nav-icon" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          ))}
        </nav>
      )}
      {/* nextcloud */}
      <button
        className="sidebar__nav-item"
        onClick={openFiles}
        title={collapsed ? 'Файлы' : ''}
      >
        <FontAwesomeIcon icon={faFolderOpen} className="sidebar__nav-icon" />
        {!collapsed && <span>Файлы</span>}
      </button>

      {/* Нижняя часть */}
      <div className="sidebar__bottom">
        {isAuthenticated && isAdmin && (
          <Link
            to="/adminpanel"
            className={`sidebar__nav-item ${location.pathname === '/adminpanel' ? 'sidebar__nav-item--active' : ''}`}
            title={collapsed ? 'Администрирование' : ''}
          >
            <FontAwesomeIcon icon={faGear} className="sidebar__nav-icon" />
            {!collapsed && <span>Настройки</span>}
          </Link>
        )}
        {isAuthenticated && (
          <button
            className="sidebar__nav-item sidebar__nav-item--logout"
            onClick={() => logout()}
            title={collapsed ? 'Выход' : ''}
          >
            <FontAwesomeIcon icon={faRightFromBracket} className="sidebar__nav-icon" />
            {!collapsed && <span>Выход</span>}
          </button>
        )}
        {!collapsed && isAuthenticated && (
          <div className="sidebar__user">
            <span>{user}</span>
            <small>{role}</small>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
